import {
  Alert,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Progress,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { IconCloudUpload, IconTrash, IconUpload } from "@tabler/icons-react";
import { useCallback, useEffect, useId, useRef, useState, type DragEvent } from "react";
import { BulkCandidateCard } from "../components/BulkCandidateCard";
import { BulkSubmitModal } from "../components/BulkSubmitModal";
import { PageHeader } from "../components/ui";
import { candidatesApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { optionalGender } from "../lib/candidateForm";
import { extractDocumentText, validateDocumentFile } from "../lib/extractDocumentText";
import { NAV_ICONS } from "../lib/navCopy";
import { useCityCatalog } from "../lib/useCityCatalog";
import { useRoleFamilyCatalog } from "../lib/useRoleFamilyCatalog";
import { useSegmentCatalog } from "../lib/useSegmentCatalog";
import { useSkillCatalog } from "../lib/useSkillCatalog";
import { axioTokens } from "../theme";
import type { IntakeResumeParseResponse } from "../types/api";
import {
  BULK_ACCEPT,
  BULK_MAX_FILES,
  BULK_MAX_MB,
  BULK_PARALLEL,
  BULK_SESSION_KEY,
  BULK_SESSION_TTL_MS,
  BULK_SET_SEGMENTS_APPLY,
  BULK_SET_SEGMENTS_LABEL,
  applySegmentsToEmpty,
  createBulkItem,
  formatFileBytes,
  isBulkItemSaveable,
  mapParsedSkillCodes,
  nameFromFileName,
  payloadFromItem,
  remapBulkItemCatalogFields,
  saveAllPending,
  selectedForSubmitSet,
  serializeBulkItems,
  suggestedRoleFamily,
  suggestedSegments,
  withBulkFieldDefaults,
  type BulkItem,
  type BulkPhase,
  type BulkSessionPayload,
} from "./bulkTypes";

function restoreMessage(status: BulkItem["status"]): string | null {
  if (status === "pending" || status === "uploading" || status === "parsing") {
    return "File is no longer available. Drop the resume again to retry.";
  }
  return null;
}

function hydrateRestoredItems(saved: Omit<BulkItem, "fileRef">[]): BulkItem[] {
  return saved.map((item) => {
    const lost = restoreMessage(item.status);
    return {
      ...withBulkFieldDefaults(item),
      fileRef: null,
      status: lost ? "error" : item.status,
      errorMessage: lost ?? item.errorMessage,
    };
  });
}

function readSession(): BulkSessionPayload | null {
  try {
    const raw = sessionStorage.getItem(BULK_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BulkSessionPayload;
    if (!parsed?.items?.length || typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt >= BULK_SESSION_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

const DUPLICATE_CANDIDATE_CODE = "duplicate_candidate";
const DUPLICATE_RESUME_CODE = "duplicate_resume";
const DUPLICATE_PHONE_COPY = "Phone already in your pool";
const DUPLICATE_RESUME_COPY = "This resume was already used for another candidate.";

export function BulkUploadPage() {
  const queryClient = useQueryClient();
  const cityCatalog = useCityCatalog();
  const skillCatalog = useSkillCatalog();
  const segmentCatalog = useSegmentCatalog();
  const roleFamilyCatalog = useRoleFamilyCatalog();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const moreInputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<BulkItem[]>([]);
  const inflightRef = useRef(0);
  const savingIdsRef = useRef<Set<string>>(new Set());
  const savingAllRef = useRef(false);
  const cityResolveRef = useRef(cityCatalog.resolveFromRaw);
  const skillCodeByLabelRef = useRef(skillCatalog.codeByLabel);
  const cityCatalogReadyRef = useRef(Boolean(cityCatalog.data));
  const knownSegmentsRef = useRef<Set<string>>(new Set());
  const knownFamiliesRef = useRef<Set<string>>(new Set());

  const [phase, setPhase] = useState<BulkPhase>("drop");
  const [items, setItemsState] = useState<BulkItem[]>([]);
  const [consentGiven, setConsentGiven] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [savingIds, setSavingIds] = useState<Set<string>>(() => new Set());
  const [dragActive, setDragActive] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<BulkSessionPayload | null>(() => readSession());
  const [bulkSegments, setBulkSegments] = useState<string[]>([]);

  const setItems = useCallback((updater: BulkItem[] | ((prev: BulkItem[]) => BulkItem[])) => {
    setItemsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      itemsRef.current = next;
      return next;
    });
  }, []);

  const patchItem = useCallback((id: string, patch: Partial<BulkItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, [setItems]);

  useEffect(() => {
    cityResolveRef.current = cityCatalog.resolveFromRaw;
    skillCodeByLabelRef.current = skillCatalog.codeByLabel;
    cityCatalogReadyRef.current = Boolean(cityCatalog.data);
    knownSegmentsRef.current = new Set(segmentCatalog.flatOptions.map((row) => row.value));
    knownFamiliesRef.current = new Set(roleFamilyCatalog.options.map((row) => row.value));
  });

  useEffect(() => {
    if (pendingRestore) return;
    if (phase === "drop" || phase === "staging" || items.length === 0) {
      if (phase === "drop") sessionStorage.removeItem(BULK_SESSION_KEY);
      return;
    }
    const payload: BulkSessionPayload = {
      items: serializeBulkItems(items),
      savedAt: Date.now(),
    };
    sessionStorage.setItem(BULK_SESSION_KEY, JSON.stringify(payload));
  }, [items, phase, pendingRestore]);

  useEffect(() => {
    if (!cityCatalog.data && !skillCatalog.data) return;
    setItems((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        const remapped = remapBulkItemCatalogFields(
          item,
          cityCatalog.data ? cityResolveRef.current : null,
          skillCatalog.data ? skillCodeByLabelRef.current : null,
        );
        if (remapped !== item) changed = true;
        return remapped;
      });
      return changed ? next : prev;
    });
  }, [cityCatalog.data, skillCatalog.data, setItems]);

  const processedCount = items.filter((item) => item.status === "done" || item.status === "error").length;
  const saveableUnsavedCount = items.filter(
    (item) => item.status === "done" && item.candidateId === null && isBulkItemSaveable(item),
  ).length;
  const progressValue =
    items.length === 0 ? 0 : Math.round((processedCount / items.length) * axioTokens.number.one * axioTokens.number.ten * axioTokens.number.ten);

  function setSaveInFlight(id: string, inFlight: boolean) {
    const next = new Set(savingIdsRef.current);
    if (inFlight) next.add(id);
    else next.delete(id);
    savingIdsRef.current = next;
    setSavingIds(next);
  }

  async function saveToPool(id: string, options?: { skipInvalidate?: boolean }) {
    if (savingIdsRef.current.has(id)) return;
    const item = itemsRef.current.find((entry) => entry.id === id);
    if (!item || item.candidateId || !isBulkItemSaveable(item)) return;
    savingIdsRef.current.add(id);
    setSavingIds(new Set(savingIdsRef.current));
    patchItem(id, { submitError: null });
    try {
      const candidate = await candidatesApi.create(payloadFromItem(item));
      patchItem(id, { candidateId: candidate.id, submitError: null });
      if (!options?.skipInvalidate) {
        await queryClient.invalidateQueries({ queryKey: ["candidates"] });
      }
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "";
      const message =
        code === DUPLICATE_CANDIDATE_CODE
          ? DUPLICATE_PHONE_COPY
          : code === DUPLICATE_RESUME_CODE
            ? DUPLICATE_RESUME_COPY
            : error instanceof ApiError
              ? error.message
              : "Could not save";
      patchItem(id, { submitError: message });
    } finally {
      setSaveInFlight(id, false);
    }
  }

  async function saveAllToPool() {
    if (savingAllRef.current) return;
    savingAllRef.current = true;
    setSavingAll(true);
    try {
      await saveAllPending(
        itemsRef.current,
        (id) => saveToPool(id, { skipInvalidate: true }),
        () => {
          void queryClient.invalidateQueries({ queryKey: ["candidates"] });
        },
      );
    } finally {
      savingAllRef.current = false;
      setSavingAll(false);
    }
  }

  async function processOne(id: string) {
    const item = itemsRef.current.find((entry) => entry.id === id);
    if (!item) return;
    const file = item.fileRef;
    if (!file && !item.resumeId) {
      patchItem(id, {
        status: "error",
        errorMessage: "File is no longer available. Drop the resume again to retry.",
      });
      return;
    }

    let resumeId = item.resumeId;
    let text = "";
    if (file) {
      patchItem(id, { status: "uploading", errorMessage: null });
      try {
        const [uploadResult, extracted] = await Promise.all([
          candidatesApi.uploadResume(file),
          extractDocumentText(file, { label: "resume" }),
        ]);
        resumeId = uploadResult.resume_id;
        text = extracted;
        patchItem(id, { resumeId, status: "parsing" });
      } catch (err) {
        patchItem(id, {
          status: "error",
          errorMessage: err instanceof ApiError || err instanceof Error ? err.message : "Upload failed",
        });
        return;
      }
    } else {
      patchItem(id, {
        status: "error",
        errorMessage: "Could not re-read this resume. Drop the file again.",
      });
      return;
    }

    patchItem(id, { status: "parsing", errorMessage: null });
    try {
      const parsed: IntakeResumeParseResponse = await candidatesApi.parseResume({
        text,
        resume_id: resumeId ?? undefined,
      });
      const parsedName = parsed.full_name?.trim() || nameFromFileName(item.fileName);
      const resolvedCity = cityResolveRef.current(parsed.city);
      const mappedSkills = mapParsedSkillCodes(parsed.skills ?? [], skillCodeByLabelRef.current);
      const skillCatalogReady = skillCodeByLabelRef.current.size > 0;
      patchItem(id, {
        status: "done",
        fileRef: null,
        resumeId,
        parsedName,
        parsedPhone: parsed.phone ?? "",
        parsedEmail: parsed.email ?? "",
        parsedTitle: parsed.current_title ?? "",
        parsedExperience: parsed.years_experience,
        parsedCities: resolvedCity
          ? [resolvedCity]
          : parsed.city && !cityCatalogReadyRef.current
            ? [parsed.city]
            : [],
        parsedSkills: mappedSkills.length > 0 || skillCatalogReady ? mappedSkills : (parsed.skills ?? []),
        parsedGender: optionalGender(parsed.gender) ?? "",
        parsedSegments: suggestedSegments(parsed.segments, knownSegmentsRef.current),
        parsedRoleFamily: suggestedRoleFamily(parsed.role_family_code, knownFamiliesRef.current),
        parsedCurrentCtcLakhs: parsed.current_ctc_lakhs ?? null,
        parsedExpectedCtcLakhs: parsed.expected_ctc_lakhs ?? null,
        parsedRaw: parsed,
        errorMessage: null,
      });
    } catch (err) {
      patchItem(id, {
        status: "error",
        errorMessage: err instanceof ApiError || err instanceof Error ? err.message : "Parse failed",
      });
    }
  }

  async function processPending(ids: string[]) {
    if (ids.length === 0) return;
    inflightRef.current += 1;
    setPhase("processing");
    const chunks: string[][] = [];
    for (let index = 0; index < ids.length; index += BULK_PARALLEL) {
      chunks.push(ids.slice(index, index + BULK_PARALLEL));
    }
    for (const chunk of chunks) {
      await Promise.allSettled(chunk.map((id) => processOne(id)));
    }
    inflightRef.current -= 1;
    if (inflightRef.current === 0) setPhase("reviewing");
  }

  function takeFiles(list: FileList | null, append: boolean) {
    if (!list?.length) return;
    const current = append ? itemsRef.current : [];
    const accepted: BulkItem[] = [];
    for (const file of Array.from(list)) {
      if (current.length + accepted.length >= BULK_MAX_FILES) {
        notifications.show({
          color: "red",
          message: `Maximum ${BULK_MAX_FILES} files at a time`,
        });
        break;
      }
      const duplicate =
        current.some((item) => item.fileName === file.name) || accepted.some((item) => item.fileName === file.name);
      if (duplicate) {
        notifications.show({ message: `${file.name} already added` });
        continue;
      }
      const invalid = validateDocumentFile(file, { label: "Resume" });
      if (invalid) {
        notifications.show({ color: "red", message: invalid });
        continue;
      }
      accepted.push(createBulkItem(file));
    }
    if (accepted.length === 0) return;

    if (append) {
      setItems((prev) => [...prev, ...accepted]);
      if (phase === "reviewing" || phase === "done" || phase === "processing") {
        void processPending(accepted.map((item) => item.id));
      } else {
        setPhase("staging");
      }
      return;
    }

    setItems(accepted);
    setPhase("staging");
  }

  function removeStaged(id: string) {
    setItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      if (next.length === 0) setPhase("drop");
      return next;
    });
  }

  function restoreSession() {
    if (!pendingRestore) return;
    const restored = hydrateRestoredItems(pendingRestore.items);
    setItems(restored);
    setConsentGiven(true);
    const hasSubmit = restored.some((item) => item.submissionId);
    setPhase(hasSubmit ? "done" : "reviewing");
    setPendingRestore(null);
  }

  function dismissRestore() {
    sessionStorage.removeItem(BULK_SESSION_KEY);
    setPendingRestore(null);
  }

  function resetWorkspace() {
    sessionStorage.removeItem(BULK_SESSION_KEY);
    setItems([]);
    setConsentGiven(false);
    setSubmitOpen(false);
    inflightRef.current = 0;
    setPhase("drop");
  }

  const showWorkspace = phase === "processing" || phase === "reviewing" || phase === "submitting" || phase === "done";

  return (
    <Stack gap={axioTokens.unit[32]}>
      <PageHeader
        icon={NAV_ICONS.candidates}
        title="Bulk Resume Upload"
        subtitle="Drop up to 20 PDFs or DOCXs. Each resume opens as a named candidate card."
        actions={
          showWorkspace ? (
            <Group gap="sm">
              <Button variant="default" onClick={() => moreInputRef.current?.click()}>
                Add more resumes
              </Button>
              <Button variant="subtle" color="gray" onClick={resetWorkspace}>
                Reset workspace
              </Button>
            </Group>
          ) : undefined
        }
      />

      {pendingRestore ? (
        <Alert
          color="violet"
          variant="light"
          title="Restore previous session?"
          withCloseButton
          onClose={dismissRestore}
        >
          <Stack gap="sm">
            <Text size="sm">
              You have {pendingRestore.items.length} parsed resume{pendingRestore.items.length === 1 ? "" : "s"} from
              earlier. Restore them, or start fresh.
            </Text>
            <Group gap="sm">
              <Button size="xs" color="violet" onClick={restoreSession}>
                Restore
              </Button>
              <Button size="xs" variant="default" onClick={dismissRestore}>
                Dismiss
              </Button>
            </Group>
          </Stack>
        </Alert>
      ) : null}

      {phase === "drop" || phase === "staging" ? (
        <Stack gap="lg">
          <div
            className="bulkDropzone"
            role="button"
            tabIndex={0}
            data-drag-active={dragActive}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(event: DragEvent) => {
              event.preventDefault();
              event.stopPropagation();
              setDragActive(true);
            }}
            onDragEnter={(event: DragEvent) => {
              event.preventDefault();
              event.stopPropagation();
              setDragActive(true);
            }}
            onDragLeave={(event: DragEvent) => {
              event.preventDefault();
              event.stopPropagation();
              setDragActive(false);
            }}
            onDrop={(event: DragEvent) => {
              event.preventDefault();
              event.stopPropagation();
              setDragActive(false);
              takeFiles(event.dataTransfer.files, phase === "staging");
            }}
          >
            <input
              ref={inputRef}
              id={inputId}
              className="bulkHiddenInput"
              type="file"
              accept={BULK_ACCEPT}
              multiple
              onChange={(event) => {
                takeFiles(event.target.files, phase === "staging");
                event.target.value = "";
              }}
            />
            <IconCloudUpload size="var(--axio-unit-32)" />
            <Text fw="var(--axio-font-weight-semibold)">Drop files here, or click to browse</Text>
            <Text size="sm" c="dimmed">
              PDF or DOCX · up to {BULK_MAX_MB} MB each · max {BULK_MAX_FILES} files
            </Text>
          </div>

          {items.length > 0 ? (
            <div className="bulkFileList">
              {items.map((item) => (
                <div key={item.id} className="bulkFileRow">
                  <span className="bulkFileRowName">{item.fileName}</span>
                  <span className="bulkFileRowSize">
                    {item.fileRef ? formatFileBytes(item.fileRef.size) : ""}
                  </span>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconTrash size="var(--axio-unit-14)" />}
                    onClick={() => removeStaged(item.id)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : null}

          {items.length > 0 ? (
            <Checkbox
              checked={consentGiven}
              onChange={(event) => setConsentGiven(event.currentTarget.checked)}
              label="I confirm that all these candidates have consented to share their information with AxioHire and to be submitted for job opportunities."
            />
          ) : null}

          {items.length > 0 ? (
            <Button
              color="violet"
              w="fit-content"
              leftSection={<IconUpload size="var(--axio-unit-16)" />}
              disabled={!consentGiven || items.length === 0}
              onClick={() => void processPending(items.filter((item) => item.status === "pending").map((item) => item.id))}
            >
              Start processing {items.length} resume{items.length === 1 ? "" : "s"}
            </Button>
          ) : null}
        </Stack>
      ) : null}

      {showWorkspace ? (
        <Stack gap="lg">
          {phase === "processing" ? (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Processing {processedCount} of {items.length} resumes…
              </Text>
              <Progress value={progressValue} color="violet" />
            </Stack>
          ) : null}

          {phase === "done" ? (
            <Alert color="green" variant="light">
              {items.filter((item) => item.candidateId !== null).length} candidates saved to your pool.
            </Alert>
          ) : null}

          <div className="bulkStatStrip">
            <span>
              <strong>{items.length}</strong> total
            </span>
            <span>
              <strong>{items.filter((item) => item.candidateId !== null).length}</strong> saved to pool
            </span>
            <span>
              <strong>
                {items.filter(
                  (item) => item.status === "done" && item.candidateId === null && !isBulkItemSaveable(item),
                ).length}
              </strong>{" "}
              need info
            </span>
          </div>

          {phase !== "processing" ? (
            <Group align="flex-end">
              <MultiSelect
                label={BULK_SET_SEGMENTS_LABEL}
                data={segmentCatalog.flatOptions}
                value={bulkSegments}
                onChange={setBulkSegments}
                searchable
                clearable
                w="var(--axio-unit-360)"
              />
              <Button
                variant="default"
                disabled={bulkSegments.length === 0}
                onClick={() => setItems(applySegmentsToEmpty(itemsRef.current, bulkSegments))}
              >
                {BULK_SET_SEGMENTS_APPLY}
              </Button>
            </Group>
          ) : null}

          <SimpleGrid
            type="container"
            cols={{ base: 1, [axioTokens.containerQuery.sm]: 2 }}
            spacing="lg"
          >
            {items.map((item) => (
              <BulkCandidateCard
                key={item.id}
                item={item}
                onPatch={patchItem}
                onRetry={(id) => void processOne(id)}
                onSaveToPool={saveToPool}
                saving={savingIds.has(item.id)}
              />
            ))}
          </SimpleGrid>
        </Stack>
      ) : null}

      <input
        ref={moreInputRef}
        className="bulkHiddenInput"
        type="file"
        accept={BULK_ACCEPT}
        multiple
        onChange={(event) => {
          takeFiles(event.target.files, true);
          event.target.value = "";
        }}
      />

      {showWorkspace && phase !== "processing" ? (
        <div className="bulkBottomBar">
          <div className="bulkBottomBarStats">
            <strong>{items.filter((item) => item.candidateId !== null).length}</strong> saved to pool ·{" "}
            <strong>{selectedForSubmitSet(items).length}</strong> selected for submission
          </div>
          <Group gap="sm">
            <Button
              variant="default"
              loading={savingAll}
              disabled={
                savingAll
                || savingIds.size > 0
                || saveableUnsavedCount === 0
              }
              onClick={() => void saveAllToPool()}
            >
              Save all to pool
            </Button>
            {phase === "done" ? (
              <Button variant="default" onClick={() => setSubmitOpen(true)}>
                Submit to another job
              </Button>
            ) : (
              <Button
                color="violet"
                disabled={selectedForSubmitSet(items).length === 0}
                onClick={() => {
                  setPhase("submitting");
                  setSubmitOpen(true);
                }}
              >
                Submit {selectedForSubmitSet(items).length} selected to a job
              </Button>
            )}
          </Group>
        </div>
      ) : null}

      <BulkSubmitModal
        opened={submitOpen}
        onClose={() => {
          setSubmitOpen(false);
          setPhase(itemsRef.current.some((item) => item.submissionId) ? "done" : "reviewing");
        }}
        items={items}
        onPatch={patchItem}
        onBatchComplete={() => {
          if (itemsRef.current.some((item) => item.submissionId)) setPhase("done");
        }}
      />
    </Stack>
  );
}
